import { ThemeEvents } from '@theme/events';

const PROGRESS_THRESHOLD = 95;
const STORAGE_KEY = 'cartProgressPopupDismissed';

class CartProgressPopup {
  constructor() {
    this.popup = null;
    this.progressBar = null;
    this.progressText = null;
    this.closeButton = null;
    this.dismissed = false;
  }

  init() {
    this.dismissed = sessionStorage.getItem(STORAGE_KEY) === 'true';

    this.#createPopup();
    this.#bindEvents();
    this.refreshFromCart();
  }

  #bindEvents() {
    document.addEventListener(ThemeEvents.cartUpdate, () => {
      this.refreshFromCart();
    });

    this.closeButton?.addEventListener('click', () => {
      this.dismissed = true;
      sessionStorage.setItem(STORAGE_KEY, 'true');
      this.#hide();
    });
  }

  async refreshFromCart() {
    const cart = await this.#getCart();

    if (!cart) return;

    const totalQuantity = cart.items.reduce((total, item) => total + item.quantity, 0);
    const progressValue = Math.min((totalQuantity / PROGRESS_THRESHOLD) * 100, 100);

    this.progressBar?.style.setProperty('--progress', `${progressValue}%`);
    if (this.progressText) {
      this.progressText.textContent = `${Math.min(totalQuantity, PROGRESS_THRESHOLD)} / ${PROGRESS_THRESHOLD}`;
    }

    if (totalQuantity >= PROGRESS_THRESHOLD && !this.dismissed) {
      this.#show();
    } else {
      this.#hide();
    }
  }

  async #getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Failed to load cart for progress popup', error);
      return null;
    }
  }

  #createPopup() {
    const wrapper = document.createElement('div');
    wrapper.className = 'cart-progress-popup';
    wrapper.setAttribute('role', 'status');
    wrapper.setAttribute('aria-live', 'polite');

    wrapper.innerHTML = `
      <div class="cart-progress-popup__content">
        <div class="cart-progress-popup__header">
          <p class="cart-progress-popup__message">
            You've added 95 cards to your cart - Checkout now to avoid any errors and use code "combineshipemail" to continue shopping.
          </p>
          <button class="cart-progress-popup__close" type="button" aria-label="Dismiss notification">&times;</button>
        </div>
        <div class="cart-progress-popup__progress" aria-hidden="true">
          <div class="cart-progress-popup__progress-bar"></div>
          <span class="cart-progress-popup__progress-text"></span>
        </div>
      </div>
    `;

    this.popup = wrapper;
    this.progressBar = wrapper.querySelector('.cart-progress-popup__progress-bar');
    this.progressText = wrapper.querySelector('.cart-progress-popup__progress-text');
    this.closeButton = wrapper.querySelector('.cart-progress-popup__close');

    document.body.appendChild(wrapper);
    this.#hide();
  }

  #show() {
    this.popup?.classList.add('cart-progress-popup--visible');
  }

  #hide() {
    this.popup?.classList.remove('cart-progress-popup--visible');
  }
}

const initializePopup = () => {
  const popup = new CartProgressPopup();
  popup.init();
};

document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', initializePopup)
  : initializePopup();